/**
 * The platform numbers masked calling runs on.
 *
 * **The pool is why calling works at all.** A booking's call line is a rented DID held for the
 * length of its window, and a pandit with two live bookings must get two different numbers —
 * caller ID is how each side knows which booking is ringing. When the pool cannot supply a
 * distinct one the server refuses the allocation rather than sharing, so a pool smaller than
 * the concurrent booking count is a silent cap on how many bookings can call at all.
 *
 * That is why the count in use sits beside the size. It is the whole question this screen
 * answers.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useAddPoolNumber, useNumberPool, useSetPoolNumberActive } from '../lib/api/hooks/comms';

/**
 * What the numbers say about headroom.
 *
 * Exported for its own test: "8 of 10 in use" is a fact ops can act on, and the sentence has to
 * change before the pool runs out rather than after — the failure it warns about is a devotee
 * tapping Call and being told no number is free.
 */
export function headroom(total: number, inUse: number): string {
  const free = total - inUse;
  if (total === 0) {
    return 'No numbers yet. Until one is added, no booking can open a call line at all.';
  }
  if (free === 0) {
    return `All ${total} are in use. The next booking to tap Call will be refused a number rather than given one already ringing for somebody else.`;
  }
  if (free <= 2) {
    return `${free} of ${total} free. Rent more before they run out — a shared number cannot be told apart by whoever answers.`;
  }
  return `${free} of ${total} free.`;
}

/**
 * Why `raw` is not a number the allocator can hand out, or null when it is (or still blank).
 * The server only bounds the length, and a typo that got in would be dialled for real bookings
 * until somebody noticed and retired it — so the shape is checked before Add is live.
 */
export function poolNumberError(raw: string): string | null {
  const phone = raw.trim();
  if (phone === '' || /^\+[1-9]\d{7,14}$/.test(phone)) return null;
  return 'A + and the country code, then digits only — e.g. +918030000123';
}

export function CommsPool() {
  const { scope, toast } = useConsole();
  const [phone, setPhone] = useState('');
  const pool = useNumberPool();
  const add = useAddPoolNumber();
  const setActive = useSetPoolNumberActive();

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  return (
    <>
      {pool.isPending && <LoadingState message="Reading the number pool…" />}
      {pool.isError && (
        <ErrorState title="Could not read the number pool" cause={errorMessage(pool.error)} />
      )}

      {pool.isSuccess && (
        <>
          <Section
            title="Headroom"
            note="A booking holds its number for the length of its window; a pandit with two live bookings needs two"
          >
            <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)' }}>
              {headroom(pool.data.items.length, pool.data.in_use)}
            </p>
          </Section>

          <Section title="Add a rented number" note="E.164, exactly as the provider issued it">
            <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end' }}>
              <Input
                label="Number"
                value={phone}
                onChange={setPhone}
                placeholder="+918030000123"
                error={poolNumberError(phone) ?? undefined}
              />
              <ScopeAction
                scope={scope}
                need="support"
                size="sm"
                disabled={phone.trim() === '' || poolNumberError(phone) !== null}
                onClick={() =>
                  add.mutate(
                    { phone: phone.trim() },
                    {
                      onSuccess: () => {
                        setPhone('');
                        toast({ tone: 'success', message: 'Added to the pool' });
                      },
                      onError: fail,
                    },
                  )
                }
              >
                Add
              </ScopeAction>
            </div>
          </Section>

          {pool.data.items.length === 0 ? (
            <EmptyState
              title="The pool is empty"
              body="Rent numbers from the telephony provider and add them here. Until one exists, tapping Call on any booking is refused."
            />
          ) : (
            <DataTable
              density="compact"
              columns={[
                { key: 'phone', label: 'Number', mono: true },
                { key: 'provider', label: 'Provider' },
                { key: 'state', label: 'State' },
                { key: 'action', label: '', align: 'right' },
              ]}
              rows={pool.data.items.map((n) => ({
                id: n.id,
                phone: n.phone,
                provider: n.provider,
                state: (
                  <StatusChip
                    tone={n.is_active ? 'success' : 'neutral'}
                    label={n.is_active ? 'active' : 'retired'}
                  />
                ),
                action: (
                  <ScopeAction
                    scope={scope}
                    need="support"
                    size="sm"
                    variant={n.is_active ? 'ghost' : 'secondary'}
                    onClick={() =>
                      setActive.mutate(
                        { id: n.id, isActive: !n.is_active },
                        {
                          onSuccess: () =>
                            toast({
                              tone: 'info',
                              message: n.is_active
                                ? `${n.phone} retired · a call already on it keeps it until its window closes`
                                : `${n.phone} is back in the pool`,
                            }),
                          onError: fail,
                        },
                      )
                    }
                  >
                    {n.is_active ? 'Retire' : 'Bring back'}
                  </ScopeAction>
                ),
              }))}
              footer={`${pool.data.items.length} number${pool.data.items.length === 1 ? '' : 's'} · ${pool.data.in_use} in use`}
            />
          )}
        </>
      )}
    </>
  );
}
